import { useEffect, useRef } from 'react'

export type LiveTextSelector = () => string

type LiveTextProps = {
    subscribe: (listener: () => void) => () => void
    selectText: LiveTextSelector
    selectColor?: LiveTextSelector
    className?: string
    style?: React.CSSProperties
}

/**
 * Renders a stable <span> and updates text/color via DOM when subscribed sources change.
 * Parent shells do not re-render on high-frequency updates.
 */
const LiveText = ({ subscribe, selectText, selectColor, className, style }: LiveTextProps) => {
    const ref = useRef<HTMLSpanElement>(null)
    const selectTextRef = useRef(selectText)
    const selectColorRef = useRef(selectColor)
    const lastTextRef = useRef<string | null>(null)
    const lastColorRef = useRef<string | undefined>(undefined)

    selectTextRef.current = selectText
    selectColorRef.current = selectColor

    useEffect(() => {
        const apply = () => {
            const el = ref.current
            if (!el) return
            const text = selectTextRef.current()
            if (lastTextRef.current !== text) {
                el.textContent = text
                lastTextRef.current = text
            }
            if (selectColorRef.current) {
                const color = selectColorRef.current()
                if (lastColorRef.current !== color) {
                    el.style.color = color ?? ''
                    lastColorRef.current = color
                }
            } else if (lastColorRef.current !== undefined) {
                el.style.removeProperty('color')
                lastColorRef.current = undefined
            }
        }
        apply()
        return subscribe(apply)
    }, [subscribe])

    return <span ref={ref} className={className} style={style} />
}

export default LiveText
